import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import api, { apiError } from '../api';
import { displayLanguage, isRTL } from '../languages';
import DeltaRenderer from './DeltaRenderer';

const POLL_MS = 10000;

export default function Reader() {
  const { roomCode } = useParams();
  const query = new URLSearchParams(useLocation().search);
  const language = query.get('lang') || 'English';
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reverse, setReverse] = useState(true);
  const blocksRef = useRef([]);

  const fetchContent = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const response = await api.get(`/content/${encodeURIComponent(roomCode)}`, {
        params: { lang: language },
        headers: { 'Cache-Control': 'no-cache' },
      });
      const nextBlocks = response.data.blocks || [];
      if (JSON.stringify(nextBlocks) !== JSON.stringify(blocksRef.current)) {
        blocksRef.current = nextBlocks;
        setBlocks(nextBlocks);
      }
      setError('');
    } catch (requestError) {
      setError(apiError(requestError, 'Could not load this room.'));
    } finally {
      if (initial) setLoading(false);
    }
  }, [language, roomCode]);

  useEffect(() => {
    fetchContent(true);
    const interval = window.setInterval(() => fetchContent(false), POLL_MS);
    return () => window.clearInterval(interval);
  }, [fetchContent]);

  const orderedBlocks = reverse ? [...blocks].reverse() : blocks;
  const rtl = isRTL(language);

  return (
    <div className="reader-page" dir={rtl ? 'rtl' : 'ltr'}>
      <div className="reader-header page-subheader">
        <h2>Room <span className="room-code-badge">{roomCode}</span> - {displayLanguage(language)}</h2>
        {blocks.length > 0 && <button type="button" className="btn btn-link block-order-toggle" onClick={() => setReverse((value) => !value)}>{reverse ? 'Newest First ↓' : 'Oldest First ↑'}</button>}
      </div>
      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {loading && <div className="status-panel" role="status">Loading translations in {displayLanguage(language)}...</div>}
      {!loading && !blocks.length && !error && <div className="status-panel">No content available yet, or translations are loading.</div>}
      <div className="reader-blocks" aria-live="polite">
        {orderedBlocks.map((block) => (
          <article className="reader-block" key={block.block_id}>
            {displayLanguage(language) === 'English' ? <DeltaRenderer content={block.content} /> : (
              <>
                {block.translation_status === 'ready' && <p className="translated-content">{block.translated_content}</p>}
                {block.translation_status === 'pending' && <p className="translation-pending" role="status">Translation in progress...</p>}
                {block.translation_status === 'failed' && <p className="translation-failed" role="status">Translation not available</p>}
              </>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
